import React, { useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import axiosInstance from "../../utils/axiosConfig";
import { useTranslation } from "react-i18next";

ChartJS.register(ArcElement, Tooltip, Legend);

const StatusDashboard = () => {
  const { t } = useTranslation();

  const [statusData, setStatusData] = useState({
    pending: 0,
    assigned: 0,
    in_progress: 0,
    completed: 0,
    out_of_service: 0,
  });
  const [timeFilter, setTimeFilter] = useState("all");
  const [availableMonths, setAvailableMonths] = useState([]);

  const fetchAvailableMonths = async () => {
    try {
      const response = await axiosInstance.get("/schedules/light");
      const dates = response.data.map(
        (schedule) => new Date(schedule.start_time)
      );

      const years = [...new Set(dates.map((date) => date.getFullYear()))].sort(
        (a, b) => b - a
      );

      const monthlyOptionsByYear = dates.reduce((acc, date) => {
        const year = date.getFullYear();
        const month = date.toLocaleString("default", { month: "long" });
        const key = `${year}${date.getMonth().toString().padStart(2, "0")}`;
        acc[key] = {
          value: `${month} ${year}`,
          label: `${month} ${year}`,
          sortKey: key,
        };
        return acc;
      }, {});

      const allOptions = [
        { value: "all", label: t("allTime") },
        ...years.map((year) => ({
          value: `year_${year}`,
          label: `${t("year")} ${year}`,
        })),
        ...Object.values(monthlyOptionsByYear)
          .sort((a, b) => b.sortKey - a.sortKey)
          .map(({ value, label }) => ({
            value,
            label,
          })),
      ];

      setAvailableMonths(allOptions);
    } catch (error) {
      console.error("Error fetching available months:", error);
    }
  };

  const fetchStatusStats = async () => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/schedules/light`;

      if (timeFilter !== "all" && timeFilter !== "year") {
        const [month, year] = timeFilter.split(" ");
        url += `?month=${month}&year=${year}`;
      } else if (timeFilter === "year") {
        url += `?period=year`;
      }

      const { data } = await axiosInstance.get(url);

      const stats = data.reduce(
        (acc, schedule) => {
          if (schedule.status) {
            acc[schedule.status] = (acc[schedule.status] || 0) + 1;
          }
          return acc;
        },
        {
          pending: 0,
          assigned: 0,
          in_progress: 0,
          completed: 0,
          out_of_service: 0,
        }
      );

      setStatusData(stats);
    } catch (error) {
      console.error("Error fetching status stats:", error);
    }
  };

  const chartData = {
    labels: [
      t("pending"),
      t("assigned"),
      t("in_progress"),
      t("completed"),
      t("out_of_service"),
    ],
    datasets: [
      {
        data: [
          statusData.pending || 0,
          statusData.assigned || 0,
          statusData.in_progress || 0,
          statusData.completed || 0,
          statusData.out_of_service || 0,
        ],
        backgroundColor: [
          "#FFC107", // Pending - Amarillo
          "#9C27B0", // Assigned - Púrpura
          "#2196F3", // In Progress - Azul
          "#4CAF50", // Completed - Verde
          "#F44336", // Out Of Service - Rojo
        ],
        borderColor: ["#FFB300", "#7B1FA2", "#1976D2", "#388E3C", "#D32F2F"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            size: 14,
            family: "'Roboto', sans-serif",
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  useEffect(() => {
    fetchStatusStats();
  }, [timeFilter]);

  return (
    <div
      style={{
        backgroundColor: "#FFF8E1",
        padding: "2rem",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        margin: "2rem",
      }}
    >
      {/* Header & Time Filter */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
        }}
      >
        <h2>{t("scheduleStatusDistribution")}</h2>
        <select
          value={timeFilter}
          onChange={(e) => setTimeFilter(e.target.value)}
          style={{
            padding: "0.5rem 1rem",
            borderRadius: "4px",
            border: "1px solid #FFC107",
            backgroundColor: "white",
            color: "#212121",
            fontSize: "16px",
            cursor: "pointer",
            minWidth: "200px",
          }}
        >
          {availableMonths.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Pie Chart */}
      <div
        style={{
          height: "400px",
          maxWidth: "600px",
          margin: "0 auto",
          padding: "1rem",
          backgroundColor: "white",
          borderRadius: "8px",
        }}
      >
        <Pie data={chartData} options={options} />
      </div>

      {/* Responsive Grid for Status Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginTop: "2rem",
          textAlign: "center",
        }}
      >
        {Object.entries(statusData).map(([status, count]) => (
          <div
            key={status}
            style={{
              backgroundColor: "white",
              padding: "1rem",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
            }}
          >
            <h3 style={{ color: "#212121", marginBottom: "0.5rem" }}>
              {t(status)
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ")}
            </h3>
            <p
              style={{
                color:
                  status === "pending"
                    ? "#FFC107"
                    : status === "in_progress"
                    ? "#2196F3"
                    : status === "completed"
                    ? "#4CAF50"
                    : status === "out_of_service"
                    ? "#F44336"
                    : "#9C27B0",
                fontSize: "24px",
                fontWeight: "bold",
              }}
            >
              {count}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatusDashboard;
